/**
 * The positioned, portaled menu behind every rail right-click: closes on an
 * outside click or Escape. `ContextMenuItemButtons` renders caller-supplied
 * actions inside it.
 */
import React, { useEffect, useRef } from 'react';

import { createPortal } from 'react-dom';

import { Z } from '@shared/constants/zIndex';

/** One caller-supplied action. */
export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

/** Classes for one menu button, shared so every rail menu looks the same. */
export const CONTEXT_MENU_ITEM_CLASS = 'hover:bg-accent block w-full px-3 py-1.5 text-left text-sm';

interface ShellProps {
  x: number;
  y: number;
  onClose: () => void;
  children: React.ReactNode;
}

/** A menu at (x, y) that closes itself on an outside click or Escape. */
export const ContextMenuShell: React.FC<ShellProps> = ({ x, y, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="border-border bg-background fixed min-w-[160px] rounded border py-1 shadow-md"
      style={{ left: x, top: y, zIndex: Z.MODAL }}
      role="menu"
    >
      {children}
    </div>,
    document.body
  );
};

/** Menu buttons for caller-supplied actions; each closes the menu after running. */
export const ContextMenuItemButtons: React.FC<{
  items: ContextMenuItem[];
  onClose: () => void;
}> = ({ items, onClose }) => (
  <>
    {items.map(item => (
      <button
        key={item.label}
        type="button"
        role="menuitem"
        disabled={item.disabled}
        onClick={() => {
          item.onClick();
          onClose();
        }}
        className={`${CONTEXT_MENU_ITEM_CLASS} disabled:opacity-50`}
      >
        {item.label}
      </button>
    ))}
  </>
);
